<script setup>
import { slugifyString } from '~/utils/slugify'

const props = defineProps({
  to: {
    type: String,
  },
  image: {
    type: Object,
    required: true,
  },
  stopper: {
    type: Object,
  },
  content: {
    type: Object,
  },
  flipped: {
    type: Boolean,
    default: false,
  },
})
</script>

<template>
  <div class="row">
    <div class="col-12">
      <component
        :is="to ? 'NuxtLink' : 'div'"
        :to="props.to"
        class="image-text d-flex flex-column flex-640-row"
        :class="{
          'image-text--flipped': props.flipped,
        }"
      >
        <div class="image-text__image">
          <div class="image-text__image-shape">
            <NuxtImg
              v-if="props.image?.src"
              sizes="sm:340px md:440px lg:800px"
              densities="1x 2x"
              :src="props.image?.src"
              :alt="props.image?.alt"
              class=""
              loading="lazy"
              format="webp"
              :placeholder="[300, 200, 60, 2]"
            />
          </div>
          <div
            v-if="props.stopper?.src"
            class="image-text__stopper"
          >
            <NuxtImg
              sizes="sm:340px md:440px lg:800px"
              :src="props.stopper?.src"
              :alt="props.stopper?.alt"
              loading="lazy"
              format="webp"
              class="img-fluid"
              :placeholder="[300, 200, 60, 2]"
            />
          </div>
        </div>
        <div class="image-text__content">
          <div
            v-if="props.content?.info || props.content?.title"
            class="image-text__header"
          >
            <div
              v-if="props.content?.info"
              class="equal-small-2 equal-small-2--bold-uppercase image-text__info"
            >
              {{ props.content?.info }}
            </div>
            <h2
              v-if="props.content?.title"
              :id="slugifyString(props.content?.title)"
              class="scale-3 scale-3--bold-uppercase"
            >
              {{ props.content?.title }}
            </h2>
          </div>
          <slot>
            <div class="equal-base image-text__text">
              {{ props.content?.text }}
            </div>
          </slot>
          <FormButton
            v-if="props.content?.button?.label"
            class="l-button--outlined image-text__button"
            :label="props.content.button.label"
            :to="props.content.button.to"
            :rel="props.content.button.rel"
            :component="props.to ? 'span' : 'NuxtLink'"
          />
        </div>
      </component>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/scss/variables.scss';
@import 'bootstrap/scss/mixins';

.image-text {
  min-height: clamp(240px, 11.11vw + 204.44px, 400px);
  background-color: $color-dark-03;
  transition: 0.5s ease;
  text-decoration: none;
  border-radius: 12px;

  &:hover,
  &:focus,
  &:active {
    background-color: $color-dark-02;
    transition: 0.5s ease;

    .image-text__image-shape {
      img {
        transform: scale(1.05);
        transition: 0.5s ease;
      }
    }
  }

  &--flipped {
    @include media-breakpoint-up(640) {
      flex-direction: row-reverse !important;
    }

    .image-text__stopper {
      right: unset;
      left: -30px;

      @include media-breakpoint-down(640) {
        top: unset;
        bottom: -15px;
        right: 12px;
        left: unset;
      }
    }

    .image-text__image-shape {
      border-top-right-radius: 12px;
      border-bottom-right-radius: 12px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;

      @include media-breakpoint-down(640) {
        border-top-left-radius: 12px;
        border-top-right-radius: 12px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }

  &__image {
    position: relative;
    width: 50%;

    @include media-breakpoint-down(640) {
      width: 100%;
    }

    img {
      overflow: hidden;
      object-fit: cover;
      width: 100%;
      height: 100%;
      transition: 0.5s ease;
    }
  }

  &__image-shape {
    height: 100%;
    width: 100%;
    overflow: hidden;

    border-top-left-radius: 12px;
    border-top-right-radius: 0;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 0;

    @include media-breakpoint-down(640) {
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  &__stopper {
    position: absolute;
    display: flex;
    top: 20px;
    right: -30px;
    max-height: clamp(90px, 6.25vw + 70px, 180px);
    max-width: clamp(90px, 6.25vw + 70px, 180px);
    z-index: 10;

    @include media-breakpoint-down(640) {
      top: unset;
      bottom: -15px;
      right: 12px;
    }
  }

  &__content {
    width: 50%;
    padding: 50px;
    margin: auto;
    display: flex;
    flex-direction: column;

    @include media-breakpoint-down(640) {
      padding: 20px;
      width: 100%;
      height: auto;
    }
  }

  &__header {
    margin-bottom: 5px;
  }

  &__info {
    color: $color-disabled;
  }

  &__text {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__button {
    margin-top: 15px;
  }
}
</style>
